import React from 'react';
import { ToastContainer, toast, Id, ToastPosition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CheckIcon, InfoIcon, WarningStrokedIcon } from '../../assets/icons';
import { BLUE_100, MINT_100, RED_100 } from '../constants/colors';

const DEFAULT_DURATION = 2000;
const DEFAULT_CONTAINER_ID = 'gr-common-toast';
const DEFAULT_POSITION: ToastPosition = 'top-center';

type ToastVariant = 'error' | 'success' | 'info';

export type { ToastPosition, ToastVariant };

export type ToastProps = {
  containerId?: string;
  position?: ToastPosition;
  newestOnTop?: boolean;
  limit?: number;
};

export type ShowToastOptions = {
  message: string;
  duration?: number;
  variant?: ToastVariant;
  position?: ToastPosition;
  icon?: React.ReactNode;
  toastId?: Id;
  containerId?: string;
  onClose?: () => void;
};

const Toast = ({
  containerId = DEFAULT_CONTAINER_ID,
  position = DEFAULT_POSITION,
  newestOnTop = true,
  limit = 3,
}: ToastProps) => {
  return React.createElement(
    React.Fragment,
    null,
    React.createElement('style', null, toastStyles),
    React.createElement(ToastContainer as any, {
      containerId,
      position,
      autoClose: DEFAULT_DURATION,
      newestOnTop,
      limit,
      closeButton: false,
      hideProgressBar: true,
      pauseOnHover: true,
      pauseOnFocusLoss: true,
      draggable: true,
      draggableDirection: 'x',
      icon: false,
      toastClassName: () => 'Toastify__toast gr-toast',
      bodyClassName: () => 'Toastify__toast-body gr-toast__body',
      className: 'Toastify__toast-container gr-toast-container',
    }),
  ) as JSX.Element;
};

const getVariantIcon = (variant: ToastVariant) => {
  switch (variant) {
    case 'success':
      return React.createElement(CheckIcon as any, { 'aria-hidden': true });
    case 'info':
      return React.createElement(InfoIcon as any, { 'aria-hidden': true });
    case 'error':
    default:
      return React.createElement(WarningStrokedIcon as any, { 'aria-hidden': true });
  }
};

const ToastContent = ({
  message,
  variant,
  icon,
}: {
  message: string;
  variant: ToastVariant;
  icon?: React.ReactNode;
}) => {
  return React.createElement(
    'div',
    { style: contentStyle },
    React.createElement('div', { style: iconWrapStyle }, (icon as any) ?? getVariantIcon(variant)),
    React.createElement('div', { style: messageStyle }, message),
  ) as JSX.Element;
};

export const showToast = ({
  message,
  duration = DEFAULT_DURATION,
  variant = 'error',
  position = DEFAULT_POSITION,
  icon,
  toastId,
  containerId = DEFAULT_CONTAINER_ID,
  onClose,
}: ShowToastOptions) => {
  return toast(React.createElement(ToastContent, { message, variant, icon }), {
    toastId,
    containerId,
    position,
    autoClose: duration,
    closeButton: false,
    hideProgressBar: true,
    className: `Toastify__toast gr-toast gr-toast--${variant}`,
    bodyClassName: 'Toastify__toast-body gr-toast__body',
    icon: false,
    onClose,
  });
};

export const dismissToast = (toastId?: Id) => {
  toast.dismiss(toastId);
};

const toastStyles = `
  .gr-toast-container.Toastify__toast-container {
    width: calc(100% - 32px);
    max-width: 600px;
    padding: 0;
    z-index: 1400;
  }

  .gr-toast-container.Toastify__toast-container--top-center,
  .gr-toast-container.Toastify__toast-container--bottom-center {
    left: 50%;
    transform: translateX(-50%);
  }

  .gr-toast-container.Toastify__toast-container--top-center {
    top: 16px;
  }

  .gr-toast-container.Toastify__toast-container--bottom-center {
    bottom: 16px;
  }

  .gr-toast-container.Toastify__toast-container--top-left,
  .gr-toast-container.Toastify__toast-container--top-right,
  .gr-toast-container.Toastify__toast-container--bottom-left,
  .gr-toast-container.Toastify__toast-container--bottom-right {
    width: calc(100% - 32px);
    max-width: 600px;
  }

  .gr-toast.Toastify__toast {
    min-height: unset;
    margin: 0 0 8px;
    padding: 12px 16px 12px 12px;
    border-radius: 16px;
    box-shadow: 0 12px 6px rgba(54, 47, 115, 0.05);
    backdrop-filter: blur(8px);
    color: #fff;
  }

  .gr-toast--error {
    background: ${RED_100};
  }

  .gr-toast--success {
    background: ${MINT_100};
  }

  .gr-toast--info {
    background: ${BLUE_100};
  }

  .gr-toast__body.Toastify__toast-body {
    margin: 0;
    padding: 0;
  }
`;

const contentStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minWidth: 0,
};

const iconWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
};

const messageStyle: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  color: '#fff',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '16px',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export default Toast;
